import { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Calculator, DollarSign, Percent, Calendar } from 'lucide-react';
import { formatPrice } from '@/lib/formatters';

interface MortgageCalculatorProps {
  propertyPrice: number;
}

export function MortgageCalculator({ propertyPrice }: MortgageCalculatorProps) {
  const [downPaymentPercent, setDownPaymentPercent] = useState(20);
  const [interestRate, setInterestRate] = useState(5.5);
  const [amortizationYears, setAmortizationYears] = useState(25);

  const calculations = useMemo(() => {
    const downPayment = (propertyPrice * downPaymentPercent) / 100;
    const principal = propertyPrice - downPayment;
    const monthlyRate = interestRate / 100 / 12;
    const numberOfPayments = amortizationYears * 12;

    // Monthly payment calculation using standard mortgage formula
    let monthlyPayment = 0;
    if (monthlyRate > 0) {
      monthlyPayment = 
        (principal * monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) /
        (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    } else {
      monthlyPayment = principal / numberOfPayments;
    }

    const totalPayment = monthlyPayment * numberOfPayments;
    const totalInterest = totalPayment - principal;

    // CMHC insurance (required for down payment < 20%)
    let cmhcInsurance = 0;
    if (downPaymentPercent < 20) {
      let insuranceRate = 0;
      if (downPaymentPercent >= 15) insuranceRate = 0.028;
      else if (downPaymentPercent >= 10) insuranceRate = 0.031;
      else if (downPaymentPercent >= 5) insuranceRate = 0.04;
      cmhcInsurance = principal * insuranceRate;
    }

    return {
      downPayment,
      principal,
      monthlyPayment,
      totalPayment,
      totalInterest,
      cmhcInsurance,
      principalWithInsurance: principal + cmhcInsurance,
    };
  }, [propertyPrice, downPaymentPercent, interestRate, amortizationYears]);

  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calculator className="w-5 h-5 text-primary" />
          Mortgage Calculator
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Monthly Payment Display */}
        <div className="bg-primary/10 rounded-xl p-4 text-center">
          <p className="text-sm text-muted-foreground mb-1">Estimated Monthly Payment</p>
          <p className="text-3xl font-bold text-primary">
            {formatPrice(calculations.monthlyPayment)}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            Principal + Interest
          </p>
        </div>

        {/* Down Payment Slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              Down Payment
            </Label>
            <span className="text-sm font-medium">
              {downPaymentPercent}% ({formatPrice(calculations.downPayment)})
            </span>
          </div>
          <Slider
            value={[downPaymentPercent]}
            onValueChange={(v) => setDownPaymentPercent(v[0])}
            min={5}
            max={50}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>5%</span>
            <span>50%</span>
          </div>
          {downPaymentPercent < 20 && (
            <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-md">
              ⚠️ CMHC insurance required: {formatPrice(calculations.cmhcInsurance)}
            </p>
          )}
        </div>

        {/* Interest Rate */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Percent className="w-4 h-4" />
              Interest Rate
            </Label>
            <div className="flex items-center gap-1">
              <Input
                type="number"
                value={interestRate}
                onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
                className="w-20 h-8 text-right text-sm"
                step={0.1}
                min={0.1}
                max={15}
              />
              <span className="text-sm text-muted-foreground">%</span>
            </div>
          </div>
          <Slider
            value={[interestRate]}
            onValueChange={(v) => setInterestRate(v[0])}
            min={1}
            max={10}
            step={0.1}
            className="w-full"
          />
        </div>

        {/* Amortization Period */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Amortization
            </Label>
            <span className="text-sm font-medium">{amortizationYears} years</span>
          </div>
          <div className="flex gap-2">
            {[15, 20, 25, 30].map((years) => (
              <button
                key={years}
                onClick={() => setAmortizationYears(years)}
                className={`flex-1 py-2 rounded-md text-sm font-medium transition-colors ${
                  amortizationYears === years
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {years}yr
              </button>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="border-t pt-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Property Price</span>
            <span className="font-medium">{formatPrice(propertyPrice)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Down Payment</span>
            <span className="font-medium">-{formatPrice(calculations.downPayment)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Mortgage Amount</span>
            <span className="font-medium">{formatPrice(calculations.principal)}</span>
          </div>
          <div className="flex justify-between border-t pt-2">
            <span className="text-muted-foreground">Total Interest</span>
            <span className="font-medium text-amber-600">{formatPrice(calculations.totalInterest)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Cost</span>
            <span className="font-bold">{formatPrice(calculations.totalPayment + calculations.downPayment)}</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
